import { useEffect, useState, useCallback } from "react";
import {
  Typography,
  Grid,
  Button,
  TableRow,
  TableContainer,
  TableHead,
  Table,
  Paper,
  TableBody,
  Modal,
  Box,
} from "@mui/material";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import { DownloadOutlined } from "@ant-design/icons";
import { getDownloadReport, getStatusEmission } from "services/certifications.service";
import { styled } from "@mui/material/styles";
import { getDataProfile } from "services/profile.service";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

interface Props {
  startEmission: boolean;
  businessEventId: number;
  fileName: string;
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#E6E6E6",
    color: "#4D4D4D",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    color: "#4D4D4D",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: "white",
  },
  // hide last border
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

export function FileProcessing(props: Props) {
  const { startEmission, businessEventId, fileName } = props;

  const [successfullyValidatedFile, setSuccessfullyValidatedFile] = useState<boolean>(false);
  const [respStatusEmission, setRespStatusEmission] = useState<any>();
  const [fileUrl, setFileUrl] = useState<string>("");
  const [profile, setProfile] = useState<any>();

  const [initialDate, setInitialDate] = useState<Date>();
  const [finalDate, setFinalDate] = useState<Date>();
  const [open, setOpen] = useState(false);

  const cols = [
    "Evento",
    "Nombre (csv)",
    "Tipo de acción",
    "Registros",
    "Estatus",
    "Fecha de ejecución",
    "Fecha de finalización",
    "Responsable",
  ];

  const handleClose = () => setOpen(false);

  const get_statusEmission = useCallback(async (id: number) => {
    try {
      console.log('Fetching emission status for business_event_id:', id);
      const resp = await getStatusEmission(id);
      console.log('Emission status response:', resp);
      
      // Manejar diferentes estructuras de respuesta
      const result = resp.data?.resource || resp.data;
      
      if (!result) {
        console.error('No emission status data in response');
        return;
      }
      
      console.log('Emission status data:', result);
      setRespStatusEmission(result);
      
      // Verificar si la emisión ha terminado
      // Usar el mayor valor entre total_rows y (issued + failed) para manejar inconsistencias del backend
      const actualTotal = Math.max(result.total_rows || 0, (result.issued || 0) + (result.failed || 0));
      const processedCount = (result.issued || 0) + (result.failed || 0);
      
      console.log(`Progress: ${processedCount}/${actualTotal} (issued: ${result.issued}, failed: ${result.failed})`);
      
      // La emisión termina cuando hay al menos 1 registro procesado Y no hay más en cola
      // O cuando issued + failed >= total_rows (considerando inconsistencias)
      const isComplete = processedCount > 0 && (
        processedCount >= actualTotal ||
        (result.total_rows > 0 && processedCount >= result.total_rows)
      );
      
      if (isComplete) {
        console.log('Emission completed');
        setFinalDate(new Date());
        setSuccessfullyValidatedFile(true);
        get_downloadReport(id);
        
        // Si los PDFs no están completos, seguir consultando
        if (result.generated_pdf < result.issued) {
          console.log(`PDFs still generating (${result.generated_pdf}/${result.issued}), polling again in 15s`);
          setTimeout(() => {
            get_statusEmission(id);
          }, 15000);
        } else {
          console.log('All PDFs generated, process complete');
        }
      } else {
        console.log('Emission in progress, polling again in 15s');
        setTimeout(() => {
          get_statusEmission(id);
        }, 15000);
      }
    } catch (error) {
      console.error('Error fetching emission status:', error);
      // Reintentar después de un error
      setTimeout(() => {
        get_statusEmission(id);
      }, 15000);
    }
  }, []);

  const get_downloadReport = useCallback(async (id: number) => {
    try {
      console.log('Fetching download report for business_event_id:', id);
      const resp = await getDownloadReport(id);
      console.log('Download report response:', resp);
      
      // Manejar diferentes estructuras de respuesta
      const reportData = resp.data?.resource || resp.data;
      
      if (!reportData || !reportData.download_url) {
        console.error('No download URL in response:', reportData);
        alert('No se pudo obtener el enlace de descarga del reporte.');
        return;
      }
      
      console.log('Download URL:', reportData.download_url);
      setFileUrl(reportData.download_url);
    } catch (error) {
      console.error('Error fetching download report:', error);
      alert('Error al obtener el reporte. Por favor intenta de nuevo.');
    }
  }, []);

  const getProfile = useCallback(async () => {
    try {
      const resp = await getDataProfile();
      setProfile(resp.data.resource);
    } catch (error) {
    }
  }, []);

  useEffect(() => {
    if (startEmission && businessEventId) {
      const currentDateTime = new Date();
      setInitialDate(currentDateTime);
      get_statusEmission(businessEventId);
    }
  }, [startEmission, businessEventId]);

  useEffect(() => {
    getProfile();
  }, []);

  return (
    <>
      <Typography variant="h5" component="h2" style={{ fontSize: "1.1rem", marginBottom: "1rem", display: "flex" }}>
        Procesamiento de archivo{" "}
        {successfullyValidatedFile && <CheckCircleOutlineIcon style={{ color: "#6BA233", marginLeft: "1rem" }} />}
      </Typography>
      {startEmission && (
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 700 }} aria-label="customized table">
            <TableHead>
              <TableRow>
                {cols.map((col, index) => (
                  <StyledTableCell key={index}>{col}</StyledTableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              <StyledTableRow>
                <StyledTableCell>{businessEventId ?? "Sin datos"}</StyledTableCell>
                <StyledTableCell>{fileName ?? "Sin datos"}</StyledTableCell>
                <StyledTableCell>{"Emisión"}</StyledTableCell>
                <StyledTableCell>
                  {respStatusEmission
                    ? Math.max(respStatusEmission.total_rows || 0, (respStatusEmission.issued || 0) + (respStatusEmission.failed || 0))
                    : "Sin datos"}
                </StyledTableCell>
                <StyledTableCell>
                  {successfullyValidatedFile ? "Procesado" : "En proceso"}
                </StyledTableCell>
                <StyledTableCell>{initialDate ? initialDate.toLocaleString() : "Sin datos"}</StyledTableCell>
                <StyledTableCell>{finalDate ? finalDate.toLocaleString() : "Sin datos"}</StyledTableCell>
                <StyledTableCell>
                  {profile ? `${profile.first_name} ${profile.father_surname}` : "Sin datos"}
                </StyledTableCell>
              </StyledTableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <div style={startEmission ? Styles.containerValidateFile : Styles.containerUpload}>
        {startEmission ? (
          <Grid container>
            <Grid xs={3}>
              <Typography variant="h5" component="h2" sx={Styles.titleStatus}>
                Registrados{<br />}
                {respStatusEmission ? respStatusEmission.total_rows : "Sin datos"}
              </Typography>
            </Grid>
            <Grid xs={2}>
              <Typography variant="h5" component="h2" sx={Styles.titleStatus}>
                En cola{<br />}
                {respStatusEmission
                  ? Math.max(0, Math.max(respStatusEmission.total_rows || 0, (respStatusEmission.issued || 0) + (respStatusEmission.failed || 0)) - (respStatusEmission.issued || 0) - (respStatusEmission.failed || 0))
                  : "Sin datos"}
              </Typography>
            </Grid>
            <Grid xs={2}>
              <Typography variant="h5" component="h2" sx={Styles.titleStatus}>
                Generados{<br />}
              </Typography>
              <Typography variant="h5" component="h2" sx={{ ...Styles.titleStatus, color: "#6BA233" }}>
                {respStatusEmission ? respStatusEmission.issued : "Sin datos"}
              </Typography>
            </Grid>
            <Grid xs={2}>
              <Typography variant="h5" component="h2" sx={Styles.titleStatus}>
                Fallidos{" "}
              </Typography>
              <Typography variant="h5" component="h2" sx={{ ...Styles.titleStatus, color: "#DA1E28" }}>
                {respStatusEmission ? respStatusEmission.failed : "Sin datos"}
              </Typography>
            </Grid>
            <Grid xs={3}>
              <Typography variant="h5" component="h2" sx={Styles.titleStatus}>
                PDFs generados{" "}
              </Typography>
              <Typography variant="h5" component="h2" sx={{ ...Styles.titleStatus, color: "#6BA233" }}>
                {respStatusEmission ? respStatusEmission.generated_pdf : "Sin datos"}
              </Typography>
            </Grid>
          </Grid>
        ) : (
          <Typography variant="h5" component="h2" sx={Styles.textUpload}>
            No hay archivo en procesamiento
          </Typography>
        )}
      </div>
      {successfullyValidatedFile && (
        <div style={Styles.containerDownload}>
          <Typography sx={Styles.containerTitle}>¡El archivo ha sido procesado! </Typography>
          {respStatusEmission?.generated_pdf === respStatusEmission?.issued ? (
            <Button
              variant="outlined"
              color="primary"
              sx={Styles.buttonDownload}
              endIcon={<DownloadOutlined />}
              href={fileUrl}
              download
            >
              Descargar reporte
            </Button>
          ) : (
            <Button
              variant="outlined"
              color="primary"
              sx={Styles.buttonDownload}
              endIcon={<DownloadOutlined />}
              onClick={() => setOpen(true)}
            >
              Descargar reporte
            </Button>
          )}
        </div>
      )}
      <Modal
        open={open}
        onClose={handleClose}
      >
        <Box sx={Styles.contentModal}>
          <Typography id="modal-modal-title" variant="h4" component="h2">
            Aviso
          </Typography>
          <Typography id="modal-modal-description" sx={{ mt: 2 }}>
            ¿Quieres descargar el reporte sin que haya finalizado la generación de los archivos PDF?
          </Typography>
          <div style={{ display: "flex", justifyContent: "start", marginTop: "1rem" }}>
            <Button variant="contained" sx={{...Styles.buttonDownload, background: '#ec5353', ":hover": {background: 'red'}}} onClick={handleClose}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="primary"
              sx={{...Styles.buttonDownload, marginLeft: "1rem"}}
              endIcon={<DownloadOutlined />}
              href={fileUrl}
              download
            >
              Descargar reporte
            </Button>
          </div>
        </Box>
      </Modal>
    </>
  );
}

const Styles = {
  contentModal: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    boxShadow: 24,
    p: 4,
    bgcolor: "background.paper",
    borderRadius: 8,
  },
  buttonDownload: {
    textTransform: "none !important",
    border: "none",
  },
  textUpload: {
    fontSize: "1.25rem",
    color: "#B3B3B3",
  },
  containerUpload: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "2rem 0rem 2rem 0rem",
  },
  containerValidateFile: {
    backgroundColor: "#fff",
    padding: "1.5rem",
    marginTop: "1.5rem",
    borderRadius: "0.5rem",
    boxShadow: "0px 1px 2px 0px rgba(25, 57, 91, 0.30), 0px 1px 3px 1px rgba(25, 57, 91, 0.15)",
  },
  containerDownload: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: 16,
    background: "#F9F9F9",
    boxShadow: "0px 1px 3px 1px rgba(25, 57, 91, 0.15)",
    borderRadius: 8,
    marginTop: "1.5rem",
  },
  containerTitle: {
    fontSize: 16,
    color: "#4D4D4D",
  },
  textList: {
    color: "#4D4D4D",
    fontSize: 14,
  },
  iconExclamation: {
    color: "#DA1E28",
    marginRight: "0.5rem",
    fontSize: "38px",
  },
  iconCheck: {
    color: "#6BA233",
    marginRight: "0.5rem",
    fontSize: "38px",
  },
  titleStatus: {
    fontSize: 20,
    color: "#4D4D4D",
    textAlign: "center",
  },
};
